import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { ConstColor } from "src/config/styles_const/styles_const";
import GoldLink from "src/presentation/common/atom/btn/gold_link";
import { toDateString, toYMString } from "src/util/commonFuntion";
import styled from "styled-components";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import { FrontDate } from "../../domain/model/frontDate";
import { IContractRepository } from "../../domain/repository/contract_repository";
import { IApiClient, createApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";
import Progress from "../common/organism/progress";
import { TBreadcrumbItem } from "../type/common";

const apiClient: IApiClient = createApiClient();
const contractRepository: IContractRepository = createContractAPI(apiClient);

interface IProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const isAllTrue = (list: boolean[]): boolean => {
  return !list.some((element) => !element);
};

const CustomerContractOperationList: React.FC<IProps> = (props) => {
  const [contracts, setContracts] = React.useState<entity.IContract[]>([]);
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showAlertBox, setShowAlertBox] = React.useState<boolean>(false);

  React.useEffect(() => {
    try {
      contractRepository.getAllOperation(1, 10000).then((res) => {
        setContracts(res);
        setShowAlertBox(
          !isAllTrue(
            res.map((item) => item.operation!!.reportCompleted || false)
          )
        );
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.customerRoot,
    { text: `稼働報告` },
  ];

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}

      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <PageTitle>稼働一覧</PageTitle>

        {showAlertBox && (
          <AlertBox>
            稼働報告が未入力の契約があります。稼働報告ボタンより、入力をお願い致します。
          </AlertBox>
        )}

        <List>
          {contracts.map((item, i) => {
            return (
              <Item key={i}>
                <ItemSection>
                  {item.operation!!.status ===
                    entity.ContractOperation.Status.Status_InProgress && (
                    <InProgressLabel>稼働中</InProgressLabel>
                  )}
                  {item.operation!!.status ===
                    entity.ContractOperation.Status.Status_Completed && (
                    <EndLabel>稼働終了</EndLabel>
                  )}
                  <ProjectNameLabel
                    target="_blank"
                    to={`/customer/project/${item.projectID}`}
                  >
                    {item.projectName}
                  </ProjectNameLabel>
                </ItemSection>

                <DateContainer>
                  <DateLabel>
                    稼働開始日:{" "}
                    {toDateString(item.operation!!.startedDate || "")}
                  </DateLabel>

                  {item.operation!!.status ===
                    entity.ContractOperation.Status.Status_InProgress && (
                    <DateLabel>
                      稼働終了予定:{" "}
                      {toYMString(item.operation!!.endScheduledYM || "")}
                    </DateLabel>
                  )}
                  {item.operation!!.status ===
                    entity.ContractOperation.Status.Status_Completed && (
                    <DateLabel>
                      稼働終了日:{" "}
                      {toDateString(item.operation!!.endedDate || "")}
                    </DateLabel>
                  )}
                </DateContainer>

                <ItemSection>
                  <CompanyInfo>
                    <CompanyName>{item.companyName}</CompanyName>
                  </CompanyInfo>

                  <LinkInfo>
                    <BtnLinkWrapper>
                      <OperationListLink to={`/customer/operations/${item.id}`}>
                        稼働報告一覧
                      </OperationListLink>
                      {item.operation!!.status ===
                        entity.ContractOperation.Status.Status_Completed && (
                        <OperationCreateDisableLink>
                          稼働報告
                        </OperationCreateDisableLink>
                      )}
                      {item.operation!!.status !==
                        entity.ContractOperation.Status.Status_Completed &&
                        item.operation!!.reportCompleted && (
                          <OperationCreateDisableLink>
                            稼働報告
                          </OperationCreateDisableLink>
                        )}
                      {item.operation!!.status !==
                        entity.ContractOperation.Status.Status_Completed &&
                        !item.operation!!.reportCompleted && (
                          <OperationCreateLink
                            to={`/customer/operations/${item.id}`}
                          >
                            稼働報告
                          </OperationCreateLink>
                        )}
                    </BtnLinkWrapper>
                  </LinkInfo>
                </ItemSection>
              </Item>
            );
          })}
        </List>
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerContractOperationList;

const PageTitle = styled.div`
  font-size: 26px;
  font-weight: bold;
  color: #333333;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const AlertBox = styled.div`
  width: 100%;
  padding: 20px;
  background: #ce414133;
  color: #333;
  margin-top: 30px;
  margin-bottom: 40px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 4px;
`;

const List = styled.div`
  width: 100%;
`;

const Item = styled.div`
  padding: 5px 5px;
  width: 100%;
  border-bottom: 1px solid ${ConstColor.PlaceholderAndBorder};
`;

const ItemSection = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0px;
`;

const EndLabel = styled.div`
  margin-right: 10px;
  background: #a4a4a4;
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
`;

const InProgressLabel = styled.div`
  margin-right: 10px;
  background: ${ConstColor.Success};
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
`;

const DateContainer = styled.div`
  display: flex;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const DateLabel = styled.div`
  font-size: 11px;
  color: #333333;
  margin-right: 45px;
`;

const ProjectNameLabel = styled(Link)`
  font-size: 11px;
  font-weight: bold;
  color: #333333;
  margin-right: 7px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ::after {
    font-family: "Font Awesome 5 Free";
    content: "\f35d";
    margin-left: 5px;
  }
`;

const CompanyInfo = styled.div`
  flex-grow: 1;
  padding: 0px 10px;
`;

const CompanyName = styled.div`
  font-size: 15px;
  font-weight: bold;
  color: #333333;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 24px;
`;

const LinkInfo = styled.div``;

const BtnLinkWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 270px;
  height: 50px;
  flex-wrap: wrap;
`;

const OperationListLink = styled(GoldLink)`
  margin-right: 10px;
  margin-top: 10px;
  margin-right: 10px;
  font-size: 12px;
  text-align: center;
`;

const OperationCreateDisableLink = styled.div`
  color: #a4a4a4;
  margin-right: 10px;
  margin-top: 10px;
  margin-right: 10px;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  padding: 4px 20px;
`;

const OperationCreateLink = styled(GoldLink)`
  color: #ce4141;
  margin-top: 10px;
  margin-right: 5px;
  font-size: 12px;
  text-align: center;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_contract_operation_list.tsx